import { useState, useEffect, useCallback } from "react";
import { BlogMeta } from "../../../types/blog.model";
import { getBlogsByCategory } from "../../../services/category.service";
import { useLocale } from "../../../context/LocaleContext";

// ── Module-level in-memory cache ─────────────────────────────────────────────
// Keyed by `${categoryId ?? ''}:${locale}` so each combination is cached separately.
const CACHE_TTL_MS = 60_000; // 1 minute
const cache = new Map<string, { data: BlogMeta[]; expiresAt: number }>();

export function useBlogs(categoryId?: string) {
  const { locale } = useLocale();
  const cacheKey = `${categoryId ?? ''}:${locale}`;

  const getCached = useCallback(() => {
    const entry = cache.get(cacheKey);
    return entry && Date.now() < entry.expiresAt ? entry.data : null;
  }, [cacheKey]);

  const [blogs, setBlogs] = useState<BlogMeta[]>(() => getCached() ?? []);
  const [loading, setLoading] = useState(() => getCached() === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = getCached();
    if (cached) {
      setBlogs(cached);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getBlogsByCategory(categoryId, locale);

        if (!isMounted) return;

        cache.set(cacheKey, { data, expiresAt: Date.now() + CACHE_TTL_MS });
        setBlogs(data);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false;
    };
  }, [categoryId, locale, cacheKey, getCached]);

  return { blogs, loading, error };
}